import React from 'react'

import Cookie from 'js-cookie'
import { Button, Card, Divider, Form, Input, notification, Popconfirm, Select } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAt, faIdBadge, faKey, faPhone, faSave, faUserCircle } from '@fortawesome/pro-solid-svg-icons'

import { connect, mapDispatchToProps, mapStateToProps } from '../../reducers/Dispatchers'
import { validateFormInput } from '../../utils'
import { canCreateUser } from '../../utils/roles'
import { buildUpdatedUserFromResponse, updateUser } from '../../utils/api/user'
import { askPasswordReset } from '../../utils/api/auth'
import { setLanguage } from '../../utils/locale'
import { ColorsPalette } from '../../config/colors'
import { Cookies } from '../../utils/Cookie'

const FormItem = Form.Item
const Option = Select.Option

/**
 * "My profile" page of the school and hospital users: edition of the account information (identity,
 * phone, language). The role cannot be changed, it is only displayed, along with the school or the
 * institutions of the profile.
 */
class MyProfileView extends React.Component {
  state = { loading: false, passwordResetLoading: false }

  checkName = (rule, value, callback) => {
    if (!validateFormInput('name', value, true)) {
      callback(this.props.t('common.invalid_value'))
    }

    callback()
  }

  checkEmail = (rule, value, callback) => {
    if (!validateFormInput('email', value, true)) {
      callback(this.props.t('common.invalid_value'))
    }

    callback()
  }

  checkPhone = (rule, value, callback) => {
    if (value && !validateFormInput('phone', value)) {
      callback(this.props.t('common.invalid_value'))
    }

    callback()
  }

  checkLanguage = (rule, value, callback) => {
    if (!value) {
      callback(this.props.t('common.invalid_value'))
    }

    callback()
  }

  handleSubmit = e => {
    e.preventDefault()

    const { form, getUser } = this.props

    form.validateFields((err, values) => {
      if (err) { return }

      this.setState({ loading: true })

      updateUser(getUser, { id: getUser.id, ...values }).then(json => {
        if (!json || (json.status && json.status === 'error')) {
          this.setState({ loading: false })

          return
        }

        // New contract: the response returns { account, profiles } (no `data`). The current profile
        // is rebuilt with the account identity.
        const updatedUser = buildUpdatedUserFromResponse(json, getUser.id)

        if (updatedUser) {
          this.props.updateUserProfile(updatedUser)

          if (
            typeof Cookie.get('user') !== 'undefined' &&
            Cookie.get('user') !== 'undefined' &&
            typeof Cookie.get('token') !== 'undefined' &&
            Cookie.get('token') !== 'undefined'
          ) {
            Cookie.set(Cookies.USER, JSON.stringify(updatedUser), { expires: 7 })
          }
        }

        const { addTranslationForLanguage, setActiveLanguage, getActiveLanguage } = this.props

        if (getActiveLanguage !== values.language) {
          setLanguage({
            language: values.language,
            addTranslationForLanguage,
            setActiveLanguage,
            getActiveLanguage,
            getUser: () => { return this.props.getUser }
          })
        }

        notification.success({
          message: this.props.t('my_profile.update.success'),
          placement: 'bottomRight'
        })

        this.setState({ loading: false })
      })
    })
  }

  /**
   * Password reset of the current account: the server emails a link to pick a new password, the
   * only self-service path (the direct set through /api/users/{profile}/change-password is
   * reserved to the establishment admins). The route answers 200 whatever happens — including for
   * an account managed by an external SSO — so the success message stays deliberately vague about
   * what the server really did.
   */
  handlePasswordReset = () => {
    const { getUser, t } = this.props

    this.setState({ passwordResetLoading: true })

    askPasswordReset(getUser.email).then(() => {
      notification.success({
        message: t('my_profile.password_reset.success', { email: getUser.email }),
        placement: 'bottomRight'
      })
    }).catch(() => {
      notification.error({
        message: t('my_profile.password_reset.error'),
        placement: 'bottomRight'
      })
    }).finally(() => this.setState({ passwordResetLoading: false }))
  }

  renderPasswordReset = () => {
    const { getUser, t } = this.props

    return (
      <div style={{ marginTop: '24px' }}>
        <Divider />
        <h4>{t('my_profile.password_reset.title')}</h4>
        <div style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: '12px' }}>
          {t('my_profile.password_reset.description', { email: getUser.email })}
        </div>
        <Popconfirm
          title={t('my_profile.password_reset.confirm', { email: getUser.email })}
          okText={t('common.yes')}
          cancelText={t('common.no')}
          onConfirm={this.handlePasswordReset}
        >
          <Button loading={this.state.passwordResetLoading}>
            <FontAwesomeIcon icon={faKey} />
            &nbsp;{t('my_profile.password_reset.submit')}
          </Button>
        </Popconfirm>
      </div>
    )
  }

  renderProfileSummary = () => {
    const { getUser, t } = this.props

    const roles = (Array.isArray(getUser.roles) ? getUser.roles : []).filter(r => r !== 'ROLE_USER')
    const organizations = [
      getUser.school ? getUser.school.name : null,
      ...(Array.isArray(getUser.institutions) ? getUser.institutions.map(i => i.name) : [])
    ].filter(o => o)

    return (
      <div style={{ marginBottom: '16px' }}>
        <div>
          <FontAwesomeIcon icon={faUserCircle} color={ColorsPalette.BLUE_ICON} />
          &nbsp;<b>{t('my_profile.summary.role')}</b> {roles.map(r => t(r)).join(', ')}
        </div>
        {organizations.length > 0 && (
          <div style={{ color: 'rgba(0, 0, 0, 0.45)', marginTop: '4px' }}>
            {organizations.join(', ')}
          </div>
        )}
      </div>
    )
  }

  render () {
    const {
      form: { getFieldDecorator },
      getUser,
      getUser: { lastname, firstname, email, phone, language },
      getLanguages,
      t
    } = this.props

    // In self-service the backend accepts everything but the email (firstname, lastname, phone,
    // language); changing the email answers 403. The field is therefore disabled for the profiles
    // without ROLE_CREATE_USER (the unchanged value still passes the backend check).
    const canEditEmail = canCreateUser(getUser)

    const formItemLayout = {
      labelCol: { xs: { span: 24 } },
      wrapperCol: { xs: { span: 24 } }
    }

    return (
      <div className='inner-spacing'>
        <Card title={t('common.my_profile')} style={{ maxWidth: '600px' }}>
          {this.renderProfileSummary()}
          <Form onSubmit={this.handleSubmit}>
            <FormItem label={t('common.lastname')} {...formItemLayout}>
              {getFieldDecorator('lastname', { rules: [{ validator: this.checkName }], initialValue: lastname })(
                <Input prefix={<FontAwesomeIcon icon={faIdBadge} />} placeholder={t('common.lastname')} />
              )}
            </FormItem>
            <FormItem label={t('common.firstname')} {...formItemLayout}>
              {getFieldDecorator('firstname', { rules: [{ validator: this.checkName }], initialValue: firstname })(
                <Input prefix={<FontAwesomeIcon icon={faIdBadge} />} placeholder={t('common.firstname')} />
              )}
            </FormItem>
            <FormItem
              label={t('common.email_address')}
              extra={canEditEmail ? undefined : t('my_profile.email_not_editable')}
              {...formItemLayout}
            >
              {getFieldDecorator('email', { rules: [{ validator: this.checkEmail }], initialValue: email })(
                <Input prefix={<FontAwesomeIcon icon={faAt} />} placeholder={t('common.email_address')} disabled={!canEditEmail} />
              )}
            </FormItem>
            <FormItem label={t('common.phone')} {...formItemLayout}>
              {getFieldDecorator('phone', { rules: [{ validator: this.checkPhone }], initialValue: phone })(
                <Input prefix={<FontAwesomeIcon icon={faPhone} />} placeholder={t('common.phone')} />
              )}
            </FormItem>
            <FormItem label={t('common.select_language')} {...formItemLayout}>
              {getFieldDecorator('language', { rules: [{ validator: this.checkLanguage }], initialValue: language || undefined })(
                <Select placeholder={t('common.select_language')}>
                  {getLanguages.map(item => (
                    <Option key={item.code} value={item.code} title={t(item.name)}>
                      {t(item.name)}
                    </Option>
                  ))}
                </Select>
              )}
            </FormItem>
            <Button type='primary' htmlType='submit' loading={this.state.loading}>
              <FontAwesomeIcon icon={faSave} />
              &nbsp;{t('common.save')}
            </Button>
          </Form>
          {this.renderPasswordReset()}
        </Card>
      </div>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Form.create()(MyProfileView))
